import React, { useState, useEffect } from 'react';
import { smppConnectorAPI } from '../services/smppApi';
import './SMPPTLVMappings.css';

const SMPPTLVMappings = () => {
  const [connectors, setConnectors] = useState([]);
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [formData, setFormData] = useState({
    connector_id: '',
    tlv_tag: '',
    tlv_name: '',
    tlv_type: 'STRING',
    is_mandatory: false,
    default_value: '',
    description: '',
  });

  useEffect(() => {
    fetchConnectors();
  }, []);

  useEffect(() => {
    if (selectedConnector) {
      fetchMappings(selectedConnector.id);
    }
  }, [selectedConnector]);

  const fetchConnectors = async () => {
    try {
      setLoading(true);
      const response = await smppConnectorAPI.getAll();
      setConnectors(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedConnector(response.data[0]);
      }
      setError('');
    } catch (err) {
      setError('Failed to fetch connectors');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMappings = async (connectorId) => {
    try {
      const response = await smppConnectorAPI.getTLVMappings(connectorId);
      setMappings(response.data || []);
    } catch (err) {
      console.error('Failed to fetch TLV mappings:', err);
    }
  };

  const handleCreate = () => {
    setEditingMapping(null);
    setFormData({
      connector_id: selectedConnector.id,
      tlv_tag: '',
      tlv_name: '',
      tlv_type: 'STRING',
      is_mandatory: false,
      default_value: '',
      description: '',
    });
    setShowModal(true);
  };

  const handleEdit = (mapping) => {
    setEditingMapping(mapping);
    setFormData(mapping);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMapping) {
        await smppConnectorAPI.updateTLVMapping(editingMapping.id, formData);
      } else {
        await smppConnectorAPI.createTLVMapping(formData);
      }
      setShowModal(false);
      fetchMappings(selectedConnector.id);
    } catch (err) {
      setError('Failed to save TLV mapping');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this TLV mapping?')) {
      try {
        await smppConnectorAPI.deleteTLVMapping(id);
        fetchMappings(selectedConnector.id);
      } catch (err) {
        setError('Failed to delete TLV mapping');
        console.error(err);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Common TLV tags for quick reference
  const commonTLVs = [
    { tag: '0x1400', name: 'ENTITY_ID', description: 'DLT Entity ID' },
    { tag: '0x1401', name: 'TEMPLATE_ID', description: 'DLT Template ID' },
    { tag: '0x1402', name: 'PE_ID', description: 'Principal Entity ID' },
    { tag: '0x1403', name: 'TELEMARKETER_ID', description: 'Telemarketer ID' },
  ];

  if (loading) {
    return (
      <div className="smpp-tlv-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="smpp-tlv-page">
      <div className="page-header">
        <h1>🏷️ TLV Mappings Management</h1>
        <button className="btn btn-primary" onClick={handleCreate} disabled={!selectedConnector}>
          ➕ Add TLV Mapping
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="tlv-layout">
        {/* Connector Selector */}
        <div className="connector-selector">
          <h2>Select Connector</h2>
          <div className="connector-list">
            {connectors.map(connector => (
              <div
                key={connector.id}
                className={`connector-item ${selectedConnector?.id === connector.id ? 'active' : ''}`}
                onClick={() => setSelectedConnector(connector)}
              >
                <div className="connector-info">
                  <div className="connector-name">{connector.connector_name}</div>
                  <div className="operator-name">{connector.operator_name}</div>
                </div>
                <div className="mapping-count">
                  {mappings.filter(m => m.connector_id === connector.id).length} TLVs
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* TLV Mappings Table */}
        <div className="mappings-container">
          <h2>
            TLV Mappings
            {selectedConnector && ` - ${selectedConnector.connector_name}`}
          </h2>

          {selectedConnector ? (
            <>
              {mappings.length === 0 ? (
                <div className="empty-state">
                  <p>No TLV mappings configured for this connector</p>
                  <button className="btn btn-primary" onClick={handleCreate}>
                    Add First TLV Mapping
                  </button>
                </div>
              ) : (
                <div className="mappings-table">
                  <table>
                    <thead>
                      <tr>
                        <th>TLV Tag</th>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Mandatory</th>
                        <th>Default Value</th>
                        <th>Description</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappings.map(mapping => (
                        <tr key={mapping.id}>
                          <td><code>{mapping.tlv_tag}</code></td>
                          <td><strong>{mapping.tlv_name}</strong></td>
                          <td><span className="type-badge">{mapping.tlv_type}</span></td>
                          <td>
                            {mapping.is_mandatory ? (
                              <span className="badge badge-danger">Required</span>
                            ) : (
                              <span className="badge badge-secondary">Optional</span>
                            )}
                          </td>
                          <td>{mapping.default_value || '-'}</td>
                          <td className="description">{mapping.description || '-'}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn btn-sm btn-info"
                                onClick={() => handleEdit(mapping)}
                              >
                                ✏️
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(mapping.id)}
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Common TLVs Reference */}
              <div className="tlv-reference">
                <h3>📚 Common TLV Tags Reference</h3>
                <div className="reference-grid">
                  {commonTLVs.map(tlv => (
                    <div key={tlv.tag} className="reference-item">
                      <code>{tlv.tag}</code>
                      <strong>{tlv.name}</strong>
                      <span>{tlv.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <p>Select a connector to manage TLV mappings</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingMapping ? 'Edit TLV Mapping' : 'Add TLV Mapping'}</h2>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-body">
                <div className="form-group">
                  <label>TLV Tag (Hex) *</label>
                  <input
                    type="text"
                    name="tlv_tag"
                    value={formData.tlv_tag}
                    onChange={handleInputChange}
                    placeholder="e.g., 0x1400"
                    required
                  />
                  <small>Format: 0xXXXX (hexadecimal)</small>
                </div>

                <div className="form-group">
                  <label>TLV Name *</label>
                  <input
                    type="text"
                    name="tlv_name"
                    value={formData.tlv_name}
                    onChange={handleInputChange}
                    placeholder="e.g., ENTITY_ID"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>TLV Type *</label>
                  <select name="tlv_type" value={formData.tlv_type} onChange={handleInputChange}>
                    <option value="STRING">String</option>
                    <option value="INTEGER">Integer</option>
                    <option value="OCTET">Octet</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Default Value</label>
                  <input
                    type="text"
                    name="default_value"
                    value={formData.default_value}
                    onChange={handleInputChange}
                    placeholder="Optional default value"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Description of this TLV parameter"
                  />
                </div>

                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_mandatory"
                      checked={formData.is_mandatory}
                      onChange={handleInputChange}
                    />
                    <span>Mandatory (Required for all messages)</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingMapping ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SMPPTLVMappings;
